import type {
	Collection,
	CollectionDefaults,
	FindOptions,
	ResolvedCollection,
	StoreCore,
	StoreSchema,
} from '@rstore/shared';
import type { FieldSelection, RelationQuery } from '#layers/data-model/public';

interface GetSelectionOptions<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
> {
	store: TStore;
	collection: ResolvedCollection<TCollection, TCollectionDefaults, TSchema>;
	findOptions?: FindOptions<TCollection, TCollectionDefaults, TSchema>;
}

/**
 * Determines which fields to fetch, including nested relation fields and their backing primitive fields
 */
export function getSelection<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
>({ store, collection, findOptions }: GetSelectionOptions<TStore, TCollection, TSchema, TCollectionDefaults>): FieldSelection {
	const selection: FieldSelection = {
		fields: [...(findOptions?.fields ?? collection.meta?.defaultFields ?? [])],
	};

	for (const [name, query] of Object.entries(findOptions?.include ?? {})) {
		if (!query) continue;

		const relation = collection.normalizedRelations[name];

		if (!relation) {
			console.error(`Failed to find relation ${name} on collection ${collection.name}`);
			continue;
		}

		const targetCollectionNames = new Set(relation.to.map((target) => target.collection));

		if (targetCollectionNames.size > 1) {
			throw new Error("Polymorphic relations aren't yet supported in relational fetch");
		}

		if (targetCollectionNames.size === 0) continue;

		const [targetCollectionName] = targetCollectionNames;
		const nestedCollection = store.$collections.find((candidate) => candidate.name === targetCollectionName);

		if (!nestedCollection) {
			throw new Error(`Could not find collection ${targetCollectionName} for relation ${name} on collection ${collection.name}`);
		}

		const nestedOptions: FindOptions<Collection, TCollectionDefaults, TSchema> = typeof query === 'object' ? query : {};
		const relationQuery: RelationQuery = getSelection({
			store,
			collection: nestedCollection,
			findOptions: nestedOptions,
		});

		if (nestedOptions.fields === undefined && !nestedCollection.meta?.defaultFields?.length) {
			relationQuery.fields = ['*'];
		}

		const backingFields = relation.to.flatMap((target) => Object.keys(target.on));

		if (!relationQuery.fields?.includes('*')) {
			relationQuery.fields = [...new Set([...(relationQuery.fields ?? []), ...backingFields])];
		}

		if ('limit' in nestedOptions) relationQuery.limit = nestedOptions.limit;
		if ('offset' in nestedOptions) relationQuery.offset = nestedOptions.offset;
		if ('filter' in nestedOptions) relationQuery.filter = nestedOptions.filter;
		if ('sort' in nestedOptions) relationQuery.sort = nestedOptions.sort;

		selection.include ??= {};
		selection.include[name] = relationQuery;
	}

	return selection;
}
